import type { HouseId } from "@nano/game-config";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ADS,
  canWatchForHint,
  deleteMyData,
  doubleGold,
  giftStatus,
  openGiftChest,
  watchAdForDouble,
  watchAdForHint,
  type GiftOutcome,
} from "../../app/adActions.ts";
import { useApp } from "../../app/store.ts";
import { adsVi } from "../../i18n/ads.vi.ts";
import { adsEnabled, showAdPrivacyOptions, useAds } from "../../services/ads/ads.ts";
import { play } from "../../services/audio/sfx.ts";
import { openExternal } from "../../services/update/updates.ts";
import { Art, Button, Sheet } from "../components/common.tsx";

/** Where the Play listing and Settings point for the privacy policy. */
export const PRIVACY_POLICY_URL = "https://nanodev.io.vn/privacy/nano-magical-city/";

/** Rewarded ads can play right now (Play app, consent given, SDK up). */
function useAdsReady(): boolean {
  const ads = useAds();
  return adsEnabled() && ads.phase === "ready";
}

/** Inside the "out of hints" sheet: a free hint for one ad, above the gold option. */
export function HintAdOffer({ onDone }: { onDone: () => void }) {
  const ready = useAdsReady();
  const { busy } = useAds();
  const online = useApp((s) => s.ui.online);
  if (!ready || !canWatchForHint()) return null;
  return (
    <div className="ad-offer" data-testid="hint-ad">
      <p className="note">{adsVi.hint.note}</p>
      <Button
        variant="primary"
        icon="icon.play"
        disabled={busy || !online}
        onClick={() => {
          void watchAdForHint().then((ok) => ok && onDone());
        }}
        testId="hint-ad-watch"
      >
        {busy ? adsVi.calling : adsVi.hint.watch}
      </Button>
      <p className="ad-offer__or">{adsVi.hint.or}</p>
    </div>
  );
}

/** Result card of a first clear: the level's gold once more for one ad. */
export function DoubleGoldOffer({ houseId, levelId }: { houseId: HouseId; levelId: number }) {
  const ready = useAdsReady();
  const { busy } = useAds();
  const online = useApp((s) => s.ui.online);
  const [done, setDone] = useState(false);
  if (!ready || done) return null;
  const gold = doubleGold(levelId);
  return (
    <Button
      variant="gold"
      icon="icon.play"
      className="ad-double"
      disabled={busy || !online}
      onClick={() => {
        void watchAdForDouble(houseId, levelId).then((outcome) => {
          if (outcome !== "failed") setDone(true);
        });
      }}
      testId="result-double"
    >
      {busy ? adsVi.calling : adsVi.double.watch(gold)}
    </Button>
  );
}

/** The chest chip in the city HUD, under the player's name. */
export function GiftChestButton() {
  const ready = useAdsReady();
  const [open, setOpen] = useState(false);
  if (!ready) return null;
  return (
    <>
      <button
        type="button"
        className="gift-chip"
        onClick={() => {
          play("open");
          setOpen(true);
        }}
        data-testid="open-gift"
      >
        <Art id="reward.chest" className="gift-chip__icon" />
        <span className="gift-chip__label">{adsVi.gift.label}</span>
      </button>
      {/* The HUD ignores taps outside its buttons, so the sheet lives with the other sheets. */}
      {open ? createPortal(<GiftSheet onClose={() => setOpen(false)} />, document.querySelector(".app") ?? document.body) : null}
    </>
  );
}

function GiftSheet({ onClose }: { onClose: () => void }) {
  const { busy } = useAds();
  const online = useApp((s) => s.ui.online);
  const [left, setLeft] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<GiftOutcome | null>(null);
  useEffect(() => {
    let alive = true;
    void giftStatus().then((status) => alive && setLeft(status?.giftsLeftToday ?? null));
    return () => {
      alive = false;
    };
  }, []);
  const opened = outcome !== null && typeof outcome === "object";
  const empty = left === 0 || outcome === "empty";
  return (
    <Sheet title={adsVi.gift.title} {...(busy ? {} : { onClose })} className="gift-sheet" testId="gift-sheet">
      <Art id={opened ? "reward.chest-open" : "reward.chest"} className={`gift-sheet__art ${opened ? "gift-sheet__art--open" : ""}`} />
      {opened ? (
        <p className="gift-sheet__got" data-testid="gift-got">
          {adsVi.gift.got(outcome.gold)}
        </p>
      ) : outcome === "later" ? (
        <p>{adsVi.gift.later}</p>
      ) : empty ? (
        <p>{adsVi.gift.empty}</p>
      ) : (
        <p>{adsVi.gift.body(ADS.giftGold)}</p>
      )}
      {left !== null || opened ? (
        <p className="note" data-testid="gift-left">
          {adsVi.gift.left(opened ? outcome.left : (left ?? 0), ADS.giftsPerDay)}
        </p>
      ) : null}
      {!online ? <p className="note">{adsVi.needNetwork}</p> : null}
      {!empty && !opened ? (
        <Button
          variant="gold"
          icon="icon.play"
          disabled={busy || !online}
          onClick={() => {
            void openGiftChest().then((result) => {
              if (result === "failed") return;
              setOutcome(result);
              if (typeof result === "object") setLeft(result.left);
            });
          }}
          testId="gift-open"
        >
          {busy ? adsVi.calling : adsVi.gift.open}
        </Button>
      ) : null}
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {opened ? adsVi.gift.done : adsVi.gift.close}
      </Button>
    </Sheet>
  );
}

/**
 * Settings, Privacy: the policy link (Play asks for it inside the app),
 * Google's ad consent form where the law needs it, and data deletion.
 */
export function PrivacySettings() {
  const ads = useAds();
  const online = useApp((s) => s.ui.online);
  const [deleting, setDeleting] = useState(false);
  return (
    <section className="settings__group" data-testid="settings-privacy">
      <h3>{adsVi.privacy.title}</h3>
      <Button variant="secondary" onClick={() => void openExternal(PRIVACY_POLICY_URL)} testId="privacy-policy">
        {adsVi.privacy.policy}
      </Button>
      {adsEnabled() && ads.privacyOptions ? (
        <Button variant="secondary" onClick={() => void showAdPrivacyOptions()} testId="ad-privacy">
          {adsVi.privacy.adChoices}
        </Button>
      ) : null}
      <Button variant="ghost" disabled={!online} onClick={() => setDeleting(true)} testId="delete-data">
        {adsVi.privacy.deleteOpen}
      </Button>
      {!online ? <p className="note">{adsVi.privacy.deleteNeedNetwork}</p> : null}
      {deleting ? <DeleteDataSheet onClose={() => setDeleting(false)} /> : null}
    </section>
  );
}

function DeleteDataSheet({ onClose }: { onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={adsVi.privacy.deleteTitle} {...(busy ? {} : { onClose })} testId="delete-sheet">
      <p>{adsVi.privacy.deleteBody}</p>
      <p className="note">{adsVi.privacy.deleteSure}</p>
      <Button
        variant="danger"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void deleteMyData().then((ok) => {
            if (!ok) setBusy(false);
          });
        }}
        testId="delete-yes"
      >
        {adsVi.privacy.deleteYes}
      </Button>
      <Button variant="ghost" onClick={onClose} disabled={busy}>
        {adsVi.privacy.deleteNo}
      </Button>
    </Sheet>
  );
}
